import type { TranslationKeys } from "./de";

/**
 * English translations (fallback language).
 * Structure must match de.ts exactly.
 */
const en: TranslationKeys = {
  common: {
    appName: "openDesk Operator",
    navigation: {
      dashboard: "Dashboard",
      tenants: "Tenants",
      capacity: "Capacity",
      audit: "Audit Log",
      settings: "Settings",
    },
    actions: {
      save: "Save",
      cancel: "Cancel",
      delete: "Delete",
      create: "Create",
      edit: "Edit",
      refresh: "Refresh",
      confirm: "Confirm",
      back: "Back",
      next: "Next",
      close: "Close",
      search: "Search",
      filter: "Filter",
      export: "Export",
      retry: "Retry",
    },
    status: {
      ready: "Ready",
      provisioning: "Provisioning",
      degraded: "Degraded",
      error: "Error",
      pending: "Pending",
      deleting: "Deleting",
      updating: "Updating",
      unknown: "Unknown",
    },
    confirm: {
      deleteTitle: "Confirm deletion",
      deleteMessage: "Are you sure you want to delete this item? This action cannot be undone.",
      yes: "Yes, delete",
      no: "No, cancel",
    },
    theme: {
      dark: "Dark theme",
      light: "Light theme",
      toggle: "Toggle theme",
    },
    language: {
      label: "Language",
      de: "Deutsch",
      en: "English",
      toggle: "Switch language",
    },
    loading: "Loading...",
    phaseUnknown: "Unknown",
    pageLoading: "Page is loading",
    authLoading: "Authentication is loading",
    noData: "No data available",
    lastFetched: "Last updated",
    justNow: "just now",
  },
  dashboard: {
    title: "Cluster Overview",
    activeTenants: "Active Tenants",
    operatorHealth: "Operator Health",
    crdsRegistered: "CRDs Registered",
    podsRunning: "Pods Running",
    tenantStatus: "Tenant Status",
    recentEvents: "Recent Events",
    clusterResources: "Cluster Resources",
    noEvents: "No events.",
    driftDetected: "Drift detected",
    components: "Components",
  },
  detail: {
    title: "Tenant Details",
    provisioning: "Provisioning",
    appsReady: "Apps ready",
    created: "Created",
    conditions: "Conditions",
    actions: "Actions",
    startBackup: "Start backup",
    deleteTenant: "Delete tenant",
    backupStarted: "Backup started.",
    deleteConfirmTitle: "Delete tenant",
    deleteConfirmMessage: "The tenant and all associated resources will be permanently deleted.",
    typeToConfirm: "Type the name to confirm",
  },
  tenants: {
    title: "Tenants",
    create: "Create tenant",
    detail: "Tenant details",
    name: "Name",
    displayName: "Display name",
    domain: "Domain",
    edition: "Edition",
    phase: "Phase",
    apps: "Applications",
    readyApps: "Ready applications",
    totalApps: "Total applications",
    namespace: "Namespace",
    createdAt: "Created at",
    userQuota: "User quota",
    storageQuota: "Storage quota",
    branding: "Branding",
    backup: "Backup",
    triggerBackup: "Start backup",
    update: "Update",
    triggerUpdate: "Start update",
    targetVersion: "Target version",
    monitoring: "Monitoring",
    conditions: "Conditions",
    config: "Configuration",
  },
  nav: {
    main: "Main navigation",
    skipToContent: "Skip to main content",
    userMenu: "User menu",
    sovereigntyStatus: "Sovereignty status",
    cluster: "Cluster",
    domain: "Domain",
    phase: "Phase",
    installed: "Installed",
    section: {
      platform: "Platform",
      governance: "Governance",
      system: "System",
      tenant: "Tenant",
      configuration: "Configuration",
    },
    dashboard: "Dashboard",
    tenants: "Tenants",
    components: "Components",
    upgrades: "Upgrades",
    backupDr: "Backup & DR",
    platformPolicy: "Platform policy",
    monitoring: "Monitoring",
    auditLog: "Audit log",
    operator: "Operator",
    settings: "Settings",
    overview: "Overview",
    deployments: "Deployments",
    apps: "Apps",
    users: "Users & groups",
    config: "Configuration",
    iam: "IAM & auth",
    backup: "Backup",
    audit: "Audit",
  },
  ladder: {
    title: "Deploy ladder",
    subtitle:
      "Every component sits in the stage the release resolver gave it. Stages run low to high; a stage waits for the one below it.",
    pickTenant: "Pick a single tenant to draw the ladder.",
    empty: "No components in this namespace.",
    stageLabel: "Stage",
    stage: {
      s10: "Pre-migrations",
      s30: "Infrastructure",
      s50: "Applications",
      s60: "Bootstrap",
      s90: "Post-migrations",
      unstaged: "No stage",
    },
    state: {
      installed: "Installed",
      progressing: "In progress",
      failed: "Failed",
      idle: "Switched off",
    },
    detail: {
      title: "Component detail",
      none: "Nothing selected",
      hint:
        "Pick a component in the ladder to read its chart, its version, what it waits for and the values Secret that feeds it.",
      chart: "Chart",
      version: "Version",
      requires: "Waits for",
      requiresNone: "nothing",
      values: "Values",
      idleHint:
        "This component is switched off for the tenant. It never installs, and dependents treat it as satisfied so the ladder cannot deadlock.",
      readyHint:
        "The values Secret feeds the L1+L2 layers. Without it Helm installs with the chart defaults.",
    },
  },
  auth: {
    login: "Sign in",
    logout: "Sign out",
    sessionExpired: "Your session has expired. Please sign in again.",
    unauthorized: "You do not have the required permissions for this action.",
    welcome: "Welcome",
    role: {
      "platform-admin": "Platform administrator",
      "tenant-admin": "Tenant administrator",
      "tenant-user": "Tenant user",
    },
  },
  errors: {
    accessDeniedTitle: "Access denied",
    accessDenied: "Access denied. You do not have permission to view this page.",
    generic: "An unexpected error occurred.",
    network: "Network error. Please check your connection.",
    notFound: "The requested resource was not found.",
    forbidden: "You do not have permission for this action.",
    conflict: "A conflict occurred. The resource may already exist.",
    validation: "Please check your input.",
    serverError: "Server error. Please try again later.",
    timeout: "The request took too long. Please try again.",
    rateLimit: "Too many requests. Please wait a moment.",
  },
};

export default en;
